import { useEffect, useState, useCallback } from "react";
import TopBar from "@/components/TopBar";
import api, { extractError } from "@/api/client";
import useWebSocket from "@/hooks/useWebSocket";
import PushEnableBanner from "@/components/PushEnableBanner";
import MenuEditor from "@/components/MenuEditor";
import ImagePicker from "@/components/ImagePicker";
import { useAuth } from "@/contexts/AuthContext";
import { RefreshCw, ChefHat, PackageCheck, Bike, MapPin, Home, Radio, Store, UtensilsCrossed, Settings, Save, Printer, PrinterCheck, FileBarChart2, Power, PowerOff, X, Eye } from "lucide-react";
import { toast } from "sonner";
import { Link as RouterLink } from "react-router-dom";
import { printReceipt, printCancelReceipt, isAutoPrintEnabled, setAutoPrintEnabled, wasAutoPrinted, markAutoPrinted } from "@/utils/printReceipt";
import SuggestAlternativeModal from "@/components/SuggestAlternativeModal";
import { playNotificationSound } from "@/lib/notificationSound";
import { isNb55BridgeAvailable, nb55PrintText, orderToText } from "@/utils/nb55Printer";
import QuickLoginSettings from "@/components/QuickLoginSettings";
import ReportsPanel from "@/components/ReportsPanel";
import BridgeKindBadge from "@/components/BridgeKindBadge";
import ApkUpdateBanner from "@/components/ApkUpdateBanner";
import ReceiptPreviewModal from "@/components/ReceiptPreviewModal";

const COLUMNS = [
  { key: "pending",   title: "طلب جديد",       color: "bg-yellow-50 border-yellow-200",  next: "preparing", nextLabel: "بدء التحضير", icon: ChefHat },
  { key: "preparing", title: "قيد التحضير",    color: "bg-blue-50 border-blue-200",  next: "ready",     nextLabel: "جاهز",         icon: PackageCheck },
  { key: "ready",     title: "جاهز للاستلام",  color: "bg-blue-50 border-blue-200",      next: null, icon: Bike },
  { key: "assigned",  title: "مع السائق",      color: "bg-purple-50 border-purple-200",  next: null, icon: Bike },
  { key: "picked_up", title: "في الطريق",      color: "bg-primary-light border-blue-300", next: null, icon: MapPin },
  { key: "delivered", title: "تم التسليم",     color: "bg-green-50 border-green-200",    next: null, icon: Home },
];

export default function RestaurantDashboard() {
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const canOrders = perms.includes("orders");
  const canMenu = perms.includes("menu");
  const canInfo = perms.includes("info");
  const firstTab = canOrders ? "orders" : canMenu ? "menu" : canInfo ? "info" : "orders";
  const [tab, setTab] = useState(firstTab);
  const [restaurant, setRestaurant] = useState(null);
  const [orders, setOrders] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [autoPrint, setAutoPrintState] = useState(isAutoPrintEnabled());
  const [showReports, setShowReports] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [previewOrder, setPreviewOrder] = useState(null);
  const [suggestModal, setSuggestModal] = useState(null); // {order, itemIndex, itemName}

  const manualClosed = !!restaurant?.manual_closed;
  const manualReason = restaurant?.manual_closed_reason || "";

  const setShopStatus = async (closed, reason = null) => {
    setStatusBusy(true); setErr("");
    try {
      const { data } = await api.post("/restaurant/status", { closed, reason });
      setRestaurant(data);
      setShowCloseModal(false);
    } catch (e) { setErr(extractError(e) || "تعذّر تحديث حالة المحل"); }
    finally { setStatusBusy(false); }
  };

  const isAccessDenied = () => false;

  const toggleAutoPrint = () => {
    const next = !autoPrint;
    setAutoPrintEnabled(next);
    setAutoPrintState(next);
  };

  // Try to auto-print pending orders that haven't been printed yet on this device.
  const autoPrintPending = useCallback((list) => {
    if (!isAutoPrintEnabled()) return;
    // T&C §2: only auto-transition to "preparing" if a driver has already accepted
    // (or if this is a pickup order — no driver needed).
    const pendings = (list || []).filter((o) =>
      o.status === "pending" && !wasAutoPrinted(o.id) &&
      (o.fulfillment !== "delivery" || o.driver_id)
    );
    for (const o of pendings) {
      markAutoPrinted(o.id);
      // Silent print chain: bridge APK → browser iframe fallback.
      if (isNb55BridgeAvailable()) {
        try { nb55PrintText(orderToText(o)); toast.success("🖨️ تم الطباعة", { duration: 2000 }); }
        catch (e) { setErr("فشل الطباعة عبر الجسر: " + (e?.message || e)); }
        api.patch(`/restaurant/orders/${o.id}/status`, { status: "preparing" }).catch(() => {});
        continue;
      }
      printReceipt(o, { paper: "58mm" })
        .then(() => { toast.success("🖨️ تم الطباعة", { duration: 2000 }); api.patch(`/restaurant/orders/${o.id}/status`, { status: "preparing" }).catch(() => {}); })
        .catch((e) => setErr("فشل الطباعة التلقائية: " + (e?.message || e)));
    }
  }, []);

  const reprint = async (order) => {
    if (isNb55BridgeAvailable()) {
      try { nb55PrintText(orderToText(order)); toast.success("🖨️ تم الطباعة", { duration: 2000 }); return; }
      catch (e) { setErr("فشل الطباعة عبر الجسر: " + (e?.message || e)); return; }
    }
    try { await printReceipt(order, { paper: "58mm" }); toast.success("🖨️ تم الطباعة", { duration: 2000 }); }
    catch (e) { setErr(extractError(e) || "تعذّرت الطباعة"); }
  };

  const refresh = useCallback(async () => {
    try {
      const me = await api.get("/restaurant/me");
      setRestaurant(me.data.restaurant);
      if (canOrders) {
        const o = await api.get("/restaurant/orders");
        setOrders(o.data);
        autoPrintPending(o.data);
      }
    } catch (e) { setErr(extractError(e)); }
  }, [canOrders, autoPrintPending]);

  useEffect(() => { refresh(); const t = setInterval(refresh, 20000); return () => clearInterval(t); }, [refresh]);
  // Restaurant intent is always "auto-print every new order" — enforce it on
  // every mount so a stale localStorage flag can never silently disable it.
  useEffect(() => {
    if (!isAutoPrintEnabled()) {
      setAutoPrintEnabled(true);
      setAutoPrintState(true);
    }
  }, []);

  const { status: wsStatus } = useWebSocket({
    onMessage: (msg) => {
      if (msg.event?.startsWith("order.") || msg.event?.startsWith("restaurant.")) {
        // Instant print: `order.created` now carries the FULL order object (see backend
        // broadcast_order_event). We look up id under either `id` or `order_id` so both
        // event shapes work (backend fix + backward compat).
        if (msg.event === "order.created" && msg.data && isAutoPrintEnabled()) {
          const oid = msg.data.id || msg.data.order_id;
          if (oid && !wasAutoPrinted(oid)) {
            markAutoPrinted(oid);
            // Kitchen sound cue — "blob" if the order carries special notes,
            // add-ons, or allergy flags; otherwise the standard chime. Keeps
            // the kitchen alert to details that need extra attention.
            try {
              const items = Array.isArray(msg.data.items) ? msg.data.items : [];
              const hasExtras = items.some((it) => Array.isArray(it.extras) && it.extras.length > 0);
              const hasItemNotes = items.some((it) => (it.notes || "").trim().length > 0);
              const hasCustomerNote = (msg.data.customer_note || "").trim().length > 0;
              const allergyRegex = /(حساسي|allerg)/i;
              const hasAllergy = allergyRegex.test(msg.data.customer_note || "")
                || items.some((it) => allergyRegex.test(it.notes || ""));
              const special = hasExtras || hasItemNotes || hasCustomerNote || hasAllergy;
              playNotificationSound(special ? "blob" : "classic");
              if (special) toast(`🔔 طلب فيه ملاحظات خاصة${hasAllergy ? " ⚠️ (حساسية!)" : ""}`, { duration: 4000 });
            } catch { /* ignore audio failures */ }
            // Only print directly if the payload is a full order object (has items[])
            if (Array.isArray(msg.data.items) && msg.data.items.length > 0) {
              printReceipt(msg.data, { paper: "58mm" })
                .then(() => { toast.success("🖨️ تم الطباعة", { duration: 2000 }); api.patch(`/restaurant/orders/${oid}/status`, { status: "preparing" }).catch(() => {}); })
                .catch((e) => setErr("فشل الطباعة التلقائية: " + (e?.message || e)));
            }
            // If not the full doc, autoPrintPending() in refresh() will handle it.
          }
        }
        // Cancellation notice: auto-print a compact "CANCELLED" slip so the
        // kitchen sees the void even if they are already preparing. We key on
        // localStorage so a rejoin/duplicate WS event does not re-print.
        if (msg.event === "order.status_changed" && msg.data?.status === "cancelled" && isAutoPrintEnabled()) {
          const oid = msg.data.id || msg.data.order_id;
          const cancelKey = oid ? `cancel:${oid}` : null;
          if (cancelKey && !wasAutoPrinted(cancelKey)) {
            markAutoPrinted(cancelKey);
            printCancelReceipt(msg.data, { paper: "58mm" })
              .then(() => toast.success("🖨️ تم طباعة إشعار الإلغاء", { duration: 2000 }))
              .catch((e) => setErr("فشل طباعة إلغاء: " + (e?.message || e)));
          }
        }
        refresh();
      }
    },
  });

  const updateStatus = async (id, status) => {
    setBusy(true);
    try { await api.patch(`/restaurant/orders/${id}/status`, { status }); await refresh(); }
    catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  // Flag a single line-item as غير متوفر — pushes a WS event that the
  // customer's tracking page catches to show the strike-through + toast.
  // Auto-refund is applied server-side; on success we open the "suggest
  // alternative" modal so the restaurant can propose replacements.
  const markItemUnavailable = async (orderId, idx, name) => {
    if (!window.confirm(`إبلاغ الزبون بأن "${name}" غير متوفر؟ سيتم استرجاع قيمته تلقائياً.`)) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/restaurant/orders/${orderId}/items/${idx}/unavailable`);
      const amt = Number(data?.refund_amount || 0);
      if (data?.wallet_credited) {
        toast.success(`تم إبلاغ الزبون + استرداد ${amt.toFixed(2)} د.ل للمحفظة`, { duration: 3500 });
      } else if (amt > 0) {
        toast.success(`تم إبلاغ الزبون · خصم ${amt.toFixed(2)} د.ل من الإجمالي (كاش)`, { duration: 3500 });
      } else {
        toast.success(`تم إبلاغ الزبون: ${name} غير متوفر`, { duration: 2500 });
      }
      await refresh();
      // Fetch the freshest order snapshot for the suggestion modal so the
      // picked item's category/price are accurate.
      try {
        const freshOrders = await api.get("/restaurant/orders");
        const fresh = (freshOrders.data || []).find((o) => o.id === orderId);
        if (fresh) setSuggestModal({ order: fresh, itemIndex: idx, itemName: name });
      } catch { /* ignore — modal is a nice-to-have */ }
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--background)]">
      <TopBar/>
      <PushEnableBanner/>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6" data-testid="restaurant-dashboard">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold text-primary-brand mb-1">
              <Store size={14}/> لوحة المطعم
              <span className="mr-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px]" data-testid="ws-live">
                <Radio size={9}/> بث لحظي {wsStatus}
              </span>
              <span className="mr-2 inline-block"><BridgeKindBadge compact/></span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black" style={{fontFamily:'Cairo'}} data-testid="restaurant-name">{restaurant?.name || "…"}</h1>
            <div className="text-xs text-[var(--text-secondary)] mt-1">{restaurant?.address}</div>
          </div>
          <div className="flex items-center gap-2">
            {/* Manual open/close toggle — visible to any owner, independent of tab */}
            <button
              onClick={() => manualClosed ? setShopStatus(false) : setShowCloseModal(true)}
              disabled={statusBusy}
              className={`px-3 py-2 rounded-xl border flex items-center gap-2 text-sm font-black transition disabled:opacity-60 ${manualClosed ? "bg-red-600 border-red-600 text-white hover:bg-red-700" : "bg-white border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}
              title={manualClosed ? "المحل مغلق يدوياً — اضغط لإعادة الفتح" : "اضغط لإغلاق المحل مؤقتاً"}
              data-testid="btn-toggle-shop-status"
              aria-pressed={manualClosed}
            >
              {manualClosed ? <PowerOff size={14}/> : <Power size={14}/>}
              {manualClosed
                ? (manualReason ? `مغلق: ${manualReason} — إعادة الفتح` : "مغلق — إعادة الفتح")
                : "متاح — إغلاق مؤقت"}
            </button>
            <RouterLink to="/printer-setup"
              className="px-3 py-2 rounded-xl bg-sky-600 hover:bg-sky-700 text-white border border-sky-600 flex items-center gap-2 text-sm font-black transition"
              title="إعداد جسر الطابعة الحرارية (بلوتوث + MAC)"
              data-testid="btn-open-printer-setup">
              <Printer size={14}/> إعداد الطابعة
            </RouterLink>
            {canOrders && (
              <button onClick={() => setShowReports(true)} className="px-3 py-2 rounded-xl bg-primary-brand text-white border border-primary-brand flex items-center gap-2 text-sm font-bold hover:bg-blue-800" data-testid="restaurant-reports-btn">
                <FileBarChart2 size={14}/> التقارير
              </button>
            )}
            <button onClick={refresh} className="px-3 py-2 rounded-xl bg-white border border-[var(--border)] flex items-center gap-2 text-sm" data-testid="btn-refresh">
              <RefreshCw size={14}/> تحديث
            </button>
          </div>
        </div>

        {manualClosed && (
          <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 flex items-center justify-between gap-3" data-testid="manual-closed-banner">
            <div className="flex items-center gap-2 text-red-800 text-sm font-bold">
              <PowerOff size={16}/>
              <span>
                المحل مغلق يدوياً الآن — لا يستطيع الزبائن إرسال طلبات جديدة.
                {manualReason && <span className="mx-1 font-black">السبب: {manualReason}</span>}
              </span>
            </div>
            <button
              onClick={() => setShopStatus(false)}
              disabled={statusBusy}
              className="shrink-0 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black disabled:opacity-60"
              data-testid="btn-reopen-shop">
              إعادة الفتح
            </button>
          </div>
        )}

        {/* APK update banner — only renders when running inside the Talabya
            POS WebView wrapper AND a newer build is available on the server. */}
        <ApkUpdateBanner />

        {canOrders && (
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <button onClick={toggleAutoPrint}
              className={`px-3 py-2 rounded-xl flex items-center gap-2 text-sm font-bold border transition ${autoPrint ? "bg-green-600 text-white border-green-600 hover:bg-green-700" : "bg-white text-[var(--text-primary)] border-[var(--border)] hover:border-primary-brand"}`}
              title={autoPrint ? "الطباعة التلقائية مفعّلة — اضغط للإيقاف" : "الطباعة التلقائية موقفة — اضغط للتفعيل"}
              data-testid="btn-toggle-autoprint"
              aria-pressed={autoPrint}
            >
              {autoPrint ? <PrinterCheck size={14}/> : <Printer size={14}/>}
              {autoPrint ? "الطباعة التلقائية: مفعّلة" : "الطباعة التلقائية: موقفة"}
            </button>

            {/* Printer bridge setup — single entry point */}
            <RouterLink to="/printer-setup"
              className="px-3 py-2 rounded-xl flex items-center gap-2 text-sm font-black border bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700"
              title="إعداد جسر الطابعة الحرارية (بلوتوث + MAC)"
              data-testid="btn-printer-setup">
              <Printer size={14}/> إعداد الطابعة
            </RouterLink>

            <button
              onClick={() => {
                const p = orders.find((o) => o.status === "pending") || orders[0];
                if (p) reprint(p); else setErr("لا توجد طلبات لتجريب الطباعة");
              }}
              className="px-3 py-2 rounded-xl flex items-center gap-2 text-sm font-bold border bg-white border-[var(--border)] hover:border-primary-brand"
              data-testid="btn-test-print"
              title="طباعة تجريبية لآخر طلب"
            >
              <Printer size={14}/> طباعة تجريبية للطلب
            </button>
          </div>
        )}

        {err && (
          <div className="mb-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg flex items-start justify-between gap-2" data-testid="rest-err">
            <div className="flex-1">{err}</div>
          </div>
        )}

        <div className="flex gap-1 bg-white p-1 rounded-xl mb-4 talabya-shadow-soft w-fit">
          {canOrders && <button onClick={() => setTab("orders")} className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-1.5 ${tab === "orders" ? "bg-primary-brand text-white" : "text-[var(--text-secondary)]"}`} data-testid="tab-orders"><PackageCheck size={14}/> الطلبات</button>}
          {canMenu && <button onClick={() => setTab("menu")} className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-1.5 ${tab === "menu" ? "bg-primary-brand text-white" : "text-[var(--text-secondary)]"}`} data-testid="tab-menu"><UtensilsCrossed size={14}/> القائمة</button>}
          {canInfo && <button onClick={() => setTab("info")} className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-1.5 ${tab === "info" ? "bg-primary-brand text-white" : "text-[var(--text-secondary)]"}`} data-testid="tab-info"><Settings size={14}/> بيانات المطعم</button>}
        </div>

        {tab === "orders" && canOrders && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="orders-board">
            {COLUMNS.map(col => {
              const list = orders.filter(o => o.status === col.key);
              const Icon = col.icon;
              return (
                <div key={col.key} className={`rounded-2xl border-2 ${col.color} p-3`} data-testid={`col-${col.key}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-bold flex items-center gap-1.5 text-sm" style={{fontFamily:'Cairo'}}><Icon size={16}/> {col.title}</div>
                    <span className="bg-white/70 rounded-full px-2 py-0.5 text-xs font-bold">{list.length}</span>
                  </div>
                  <div className="flex flex-col gap-2">
                    {list.length === 0 ? (
                      <div className="text-xs text-[var(--text-secondary)] text-center py-4 italic">لا يوجد</div>
                    ) : list.map(o => {
                      const awaitingDriver = o.status === "pending" && o.fulfillment === "delivery" && !o.driver_id;
                      return (
                      <div key={o.id} className="bg-white rounded-xl p-3 talabya-shadow-soft" data-testid={`order-${o.id}`}>
                        <div className="flex justify-between items-start mb-2">
                          <div className="font-black text-sm" style={{fontFamily:'Cairo'}}>{o.code}</div>
                          <div className="text-xs font-bold text-primary-brand">{o.total.toFixed(2)} د.ل</div>
                        </div>
                        <div className="text-xs text-[var(--text-secondary)]">{o.customer_name} · {o.items.length} صنف · {o.fulfillment === "delivery" ? "توصيل" : "استلام"}</div>
                        <div className="mt-2 space-y-1 bg-[var(--surface-alt)] rounded-lg p-1.5" data-testid={`order-items-${o.id}`}>
                          {o.items.map((it, idx) => (
                            <div key={idx} className={`flex items-center justify-between gap-2 text-xs px-2 py-1.5 rounded-md ${it.unavailable ? "bg-red-50 border border-red-200 line-through text-red-600" : "bg-white border border-[var(--border)] text-[var(--text-primary)]"}`}
                                 data-testid={`order-item-${o.id}-${idx}`}>
                              <span className="truncate font-bold flex items-center gap-1.5">
                                <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] rounded-full bg-primary-light text-primary-brand text-[10px] font-black px-1">× {it.quantity}</span>
                                {it.name}
                              </span>
                              {it.unavailable ? (
                                <span className="shrink-0 text-[10px] font-black text-red-600 no-underline bg-red-100 px-2 py-0.5 rounded-full border border-red-300">⛔ غير متوفر</span>
                              ) : (
                                <button
                                  onClick={() => markItemUnavailable(o.id, idx, it.name)}
                                  disabled={busy}
                                  title="إبلاغ الزبون أن هذا الصنف غير متوفر — سيتم إرجاع قيمته للمحفظة تلقائياً"
                                  className="shrink-0 text-[11px] font-black text-amber-800 bg-amber-100 hover:bg-red-500 hover:text-white border border-amber-300 hover:border-red-600 px-2.5 py-1 rounded-full transition shadow-sm"
                                  data-testid={`mark-unavailable-${o.id}-${idx}`}>
                                  ⚠ غير متوفر
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                        {awaitingDriver && (
                          <div className="mt-2 text-[10px] font-black text-amber-800 bg-amber-100 border border-amber-300 rounded-lg px-2 py-1 flex items-center gap-1" data-testid={`awaiting-driver-${o.id}`}>
                            ⏳ بانتظار قبول السائق — سيُتاح بدء التحضير بعد قبوله
                          </div>
                        )}
                        {col.next && (
                          <button onClick={() => updateStatus(o.id, col.next)}
                                  disabled={busy || awaitingDriver}
                                  title={awaitingDriver ? "بانتظار قبول السائق — لا يمكن بدء التحضير الآن" : ""}
                                  className={`w-full mt-2 py-1.5 rounded-lg text-white text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed ${awaitingDriver ? "bg-gray-400" : "bg-primary-brand"}`}
                                  data-testid={`advance-${o.id}`}>
                            {col.nextLabel}
                          </button>
                        )}
                        <button onClick={() => reprint(o)} className="w-full mt-1 py-1 rounded-lg bg-[var(--surface-alt)] border border-[var(--border)] text-[var(--text-primary)] text-[11px] font-bold flex items-center justify-center gap-1 hover:bg-white" data-testid={`reprint-${o.id}`}>
                          <Printer size={11}/> طباعة الإيصال
                        </button>
                        <button onClick={() => setPreviewOrder(o)} className="w-full mt-1 py-1 rounded-lg bg-white border border-primary-brand/40 text-primary-brand text-[11px] font-bold flex items-center justify-center gap-1 hover:bg-primary-light" data-testid={`preview-receipt-${o.id}`}>
                          <Eye size={11}/> عرض الإيصال
                        </button>
                        {o.status === "pending" && (
                          <button onClick={() => updateStatus(o.id, "cancelled")} disabled={busy} className="w-full mt-1 py-1 rounded-lg bg-white border border-red-200 text-red-600 text-[11px] font-bold disabled:opacity-60" data-testid={`cancel-${o.id}`}>
                            إلغاء الطلب
                          </button>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === "menu" && canMenu && (
          <MenuEditor scope="restaurant"/>
        )}

        {tab === "info" && canInfo && (
          <div className="space-y-4">
            <RestaurantInfoEditor restaurant={restaurant} refresh={refresh}/>
            <QuickLoginSettings/>
          </div>
        )}

        {!canOrders && !canMenu && !canInfo && (
          <div className="bg-white rounded-3xl p-10 text-center talabya-shadow-soft">
            <Settings size={40} className="mx-auto text-[var(--text-secondary)] mb-3"/>
            <div className="font-bold text-lg" style={{fontFamily:'Cairo'}}>لا توجد صلاحيات مفعّلة على حسابك</div>
            <div className="text-sm text-[var(--text-secondary)] mt-1">تواصل مع إدارة طلبية لتفعيل الصلاحيات المناسبة.</div>
          </div>
        )}
      </div>
      {showReports && <ReportsPanel scope="restaurant" restaurantName={restaurant?.name} onClose={() => setShowReports(false)}/>}
      {previewOrder && <ReceiptPreviewModal order={previewOrder} onClose={() => setPreviewOrder(null)}/>}
      {suggestModal && (
        <SuggestAlternativeModal
          order={suggestModal.order}
          itemIndex={suggestModal.itemIndex}
          itemName={suggestModal.itemName}
          onClose={() => setSuggestModal(null)}
        />
      )}
      {showCloseModal && (
        <CloseShopModal
          busy={statusBusy}
          onClose={() => setShowCloseModal(false)}
          onConfirm={(reason) => setShopStatus(true, reason)}
        />
      )}
    </div>
  );
}

function RestaurantInfoEditor({ restaurant, refresh }) {
  const [form, setForm] = useState(restaurant || {});
  useEffect(() => { setForm(restaurant || {}); }, [restaurant]);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(""); const [ok, setOk] = useState("");
  const save = async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      const payload = { name: form.name, address: form.address, phone: form.phone, hours: form.hours, cuisine: form.cuisine, image: form.image, city: form.city };
      await api.patch("/restaurant/info", payload);
      setOk("تم الحفظ بنجاح");
      await refresh();
    } catch (e) { setErr(extractError(e)); } finally { setBusy(false); }
  };
  if (!restaurant) return null;
  return (
    <div className="bg-white rounded-3xl p-6 talabya-shadow-soft max-w-2xl" data-testid="info-editor">
      <div className="text-lg font-bold mb-4" style={{fontFamily:'Cairo'}}>بيانات المطعم</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input placeholder="اسم المطعم" className="px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)]" value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="info-name"/>
        <input placeholder="المدينة" className="px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)]" value={form.city || ""} onChange={e => setForm({ ...form, city: e.target.value })} data-testid="info-city"/>
        <input placeholder="العنوان" className="px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] sm:col-span-2" value={form.address || ""} onChange={e => setForm({ ...form, address: e.target.value })} data-testid="info-address"/>
        <input placeholder="الهاتف" className="px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)]" value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="info-phone"/>
        <input placeholder="ساعات العمل" className="px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)]" value={form.hours || ""} onChange={e => setForm({ ...form, hours: e.target.value })} data-testid="info-hours"/>
        <input placeholder="نوع المطبخ" className="px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)]" value={form.cuisine || ""} onChange={e => setForm({ ...form, cuisine: e.target.value })} data-testid="info-cuisine"/>
      </div>
      <div className="mt-4">
        <label className="text-sm font-bold block mb-2">صورة المطعم</label>
        <ImagePicker value={form.image} onChange={(v) => setForm({ ...form, image: v })} label="صورة المطعم"/>
      </div>
      {err && <div className="mt-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{err}</div>}
      {ok && <div className="mt-3 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg" data-testid="info-ok">{ok}</div>}
      <button onClick={save} disabled={busy} className="mt-4 px-6 py-3 rounded-xl bg-primary-brand text-white font-bold disabled:opacity-60" data-testid="info-save">
        <Save size={16} className="inline ml-2"/> حفظ التعديلات
      </button>
    </div>
  );
}


const CLOSE_REASON_PRESETS = ["استراحة", "نفدت المواد", "زحمة طلبات", "صيانة"];

function CloseShopModal({ busy, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [custom, setCustom] = useState("");
  const finalReason = (reason === "__other__" ? custom : reason).trim();

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="close-shop-modal">
      <div className="bg-white rounded-3xl w-full max-w-md p-6 talabya-shadow-float">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
              <PowerOff size={16}/>
            </div>
            <div>
              <div className="font-black text-lg" style={{fontFamily:'Cairo'}}>إغلاق المحل مؤقتاً</div>
              <div className="text-xs text-[var(--text-secondary)]">لن يستطيع الزبائن إرسال طلبات جديدة حتى إعادة الفتح.</div>
            </div>
          </div>
          <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-red-600 p-1" data-testid="close-shop-modal-x">
            <X size={18}/>
          </button>
        </div>

        <div className="mt-3">
          <label className="text-sm font-bold block mb-2">اختر سبب الإغلاق (يظهر للزبون):</label>
          <div className="grid grid-cols-2 gap-2">
            {CLOSE_REASON_PRESETS.map(r => (
              <button key={r} type="button" onClick={() => setReason(r)}
                className={`px-3 py-2 rounded-xl border text-sm font-bold ${reason === r ? "bg-primary-brand text-white border-primary-brand" : "bg-white text-[var(--text-primary)] border-[var(--border)] hover:border-primary-brand"}`}
                data-testid={`close-reason-${r}`}>
                {r}
              </button>
            ))}
            <button type="button" onClick={() => setReason("__other__")}
              className={`px-3 py-2 rounded-xl border text-sm font-bold col-span-2 ${reason === "__other__" ? "bg-primary-brand text-white border-primary-brand" : "bg-white text-[var(--text-primary)] border-[var(--border)] hover:border-primary-brand"}`}
              data-testid="close-reason-other">
              سبب آخر…
            </button>
          </div>
          {reason === "__other__" && (
            <input
              autoFocus
              maxLength={60}
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="أدخل السبب"
              className="w-full mt-2 px-4 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] text-sm"
              data-testid="close-reason-custom"
            />
          )}
        </div>

        <div className="mt-5 flex gap-2">
          <button onClick={onClose} disabled={busy} className="flex-1 py-3 rounded-xl bg-white border border-[var(--border)] font-bold disabled:opacity-60" data-testid="close-shop-cancel">
            إلغاء
          </button>
          <button
            onClick={() => onConfirm(finalReason || null)}
            disabled={busy}
            className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-black disabled:opacity-60"
            data-testid="close-shop-confirm">
            {busy ? "جارِ الإغلاق..." : "تأكيد الإغلاق"}
          </button>
        </div>
      </div>
    </div>
  );
}
